import {createAsyncThunk, createSlice, current} from "@reduxjs/toolkit";
import {AUTH, dbRef, storageRef} from "../../auth/FirebaseContext";
import {ref, uploadBytes} from "firebase/storage";
import {child, get, set, update} from "firebase/database";
import {sendPasswordResetEmail} from "firebase/auth";
import {
    getPrinterSize,
    getSessionTrainingMode,
    getSilentPrint,
    setPrinterSize,
    setSessionTrainingMode,
    setSilentPrint
} from "../../helper/session";

export const updateAdvertisementText = createAsyncThunk("updateAdvertisementText", async (data) => {
    return await update(child(dbRef, `users/${AUTH.currentUser.uid}/private/settings`),
        {advertisementBody: data ?? ""});
});

// Posle prijave ili duže neaktivnosti RTDB read ume da padne na istekao token; tada
// štampa ide bez loga i reklame iako u bazi postoje. Isti obrazac kao u userSettings.
async function refreshIdToken() {
    if (!AUTH.currentUser) return;
    try {
        await AUTH.currentUser.getIdToken(true);
    } catch (e) {
        console.error("getIdToken(true) pre učitavanja podešavanja:", e);
    }
}

export const fetchAdvertisementText = createAsyncThunk("fetchAdvertisementText", async () => {
    await refreshIdToken();
    const fromSettings = (await get(child(dbRef, `users/${AUTH.currentUser.uid}/private/settings/advertisementBody`))).val();
    if (fromSettings !== null && fromSettings !== undefined) {
        return fromSettings;
    }
    // Stari nalozi: vrednost još u private/others — pročitaj i tiho preseli.
    const legacy = (await get(child(dbRef, `users/${AUTH.currentUser.uid}/private/others/advertisementText`))).val();
    if (legacy !== null && legacy !== undefined) {
        try {
            await update(child(dbRef, `users/${AUTH.currentUser.uid}/private/settings`),
                {advertisementBody: legacy});
        } catch (e) {
            // Migracija je best-effort — tekst je svakako pročitan sa stare putanje.
        }
    }
    return legacy;
});

export const fetchContactEmails = createAsyncThunk("fetchContactEmails", async () => {
    let emails = (await get(child(dbRef, `users/${AUTH.currentUser.uid}/protected/contactEmail`))).val();
    if (emails)
        return emails.split(",");
    return [];
});

export const updateContactEmails = createAsyncThunk("updateContactEmails", async (data) => {
    return await set(child(dbRef, `users/${AUTH.currentUser.uid}/protected/contactEmail`), data);
});

export const fetchTIN = createAsyncThunk("fetchTin", async () => {
    return (await get(child(dbRef, `users/${AUTH.currentUser.uid}/profile/pub/TIN`))).val();
});

export const fetchSettings = createAsyncThunk("fetchSettings", async () => {
    await refreshIdToken();
    let settings = (await get(child(dbRef, `users/${AUTH.currentUser.uid}/private/settings`))).val();
    return {settings};
});

export const updateSettings = createAsyncThunk("updateSettings", async (data) => {
    await update(child(dbRef, `users/${AUTH.currentUser.uid}/private/settings`), data);
    return (await get(child(dbRef, `users/${AUTH.currentUser.uid}/private/settings`))).val();
});

export const insertUserImage = createAsyncThunk("insertUserImage", async (image) => {
    let imageRef = ref(ref(storageRef, "public/userphotos"), `${AUTH.currentUser.uid}.png`);
    return await uploadBytes(imageRef, image);
});

export const fetchUserLogoUrl = createAsyncThunk("fetchUserLogoUrl", async () => {
    // const imageRef = ref(ref(storageRef, "public/userphotos"), `${AUTH.currentUser.uid}.png`);
    // try {
    //   return await getDownloadURL(imageRef);
    // } catch (error) {
    //   if (error.code === "storage/object-not-found") {
    //     // Image not found, return null or any other appropriate value
    //     return null;
    //   }
    //   throw error; // Rethrow other types of errors
    // }
});


export const fetchBankAccount = createAsyncThunk("fetchBankAccount", async () => {
    return (await get(child(dbRef, `users/${AUTH.currentUser.uid}/private/bankAccount`))).val();
});

export const updateBankAccount = createAsyncThunk("updateBankAccount", async (data) => {
    return await set(child(dbRef, `users/${AUTH.currentUser.uid}/private/bankAccount`), data);
});

export const fetchAccountant = createAsyncThunk("fetchAccountant", async () => {
    return (await get(child(dbRef, `users/${AUTH.currentUser.uid}/private/accountant`))).val();
});

export const updateAccountant = createAsyncThunk("updateAccountant", async (data) => {
    return await set(child(dbRef, `users/${AUTH.currentUser.uid}/private/accountant`),
        {
            email: data.email,
            lastName: data.lastName,
            name: data.name,
            telephone: data.telephone || ""
        });
});

export const updateTicketValidityPeriod = createAsyncThunk("updateTicketValidityPeriod", async (data) => {
    return await update(child(dbRef, `users/${AUTH.currentUser.uid}/private/settings`),
        {
            ticketValidityPeriod: data
        });
});

export const updateVerificationPrice = createAsyncThunk("updateVerificationPrice", async (data) => {
    return await update(child(dbRef, `users/${AUTH.currentUser.uid}/private/settings/bussoft/web/`),
        {
            ticketPriceConfirmation: data
        });
});

export const updateSeatSelectionEnabled = createAsyncThunk("updateSeatSelectionEnabled", async (data) => {
    return await update(child(dbRef, `users/${AUTH.currentUser.uid}/private/settings/bussoft/web/`),
        {
            seatSelectionEnabled: data
        });
});

export const updateA4PrintEnabled = createAsyncThunk("updateA4PrintEnabled", async (data) => {
    return await update(child(dbRef, `users/${AUTH.currentUser.uid}/private/settings/bussoft/web/`),
        {
            a4PrintEnabled: data
        });
});

export const fetchCityPickerType = createAsyncThunk("fetchCityPickerType", async () => {
    await refreshIdToken();
    return (await get(child(dbRef, `users/${AUTH.currentUser.uid}/private/settings/bussoft/pickerType`))).val();
});

export const updateCityPickerType = createAsyncThunk("updateCityPickerType", async (data) => {
    return await update(child(dbRef, `users/${AUTH.currentUser.uid}/private/settings/bussoft/`),
        {
            pickerType: data
        });
});

export const fetchCityDepartureMode = createAsyncThunk("fetchCityDepartureMode", async () => {
    await refreshIdToken();
    return (await get(child(dbRef, `users/${AUTH.currentUser.uid}/private/settings/bussoft/departureMode`))).val();
});

export const updateCityDepartureMode = createAsyncThunk("updateCityDepartureMode", async (data) => {
    return await update(child(dbRef, `users/${AUTH.currentUser.uid}/private/settings/bussoft/`),
        {
            departureMode: data
        });
});

export const fetchCityPanicNumber = createAsyncThunk("fetchCityPanicNumber", async () => {
    await refreshIdToken();
    return (await get(child(dbRef, `users/${AUTH.currentUser.uid}/private/settings/bussoft/panicNumber`))).val();
});

export const updateCityPanicNumber = createAsyncThunk("updateCityPanicNumber", async (data) => {
    return await update(child(dbRef, `users/${AUTH.currentUser.uid}/private/settings/bussoft/`),
        {
            panicNumber: data || null
        });
});

export const requestPasswordReset = createAsyncThunk("requestPasswordReset", async () => {
    return await sendPasswordResetEmail(AUTH, AUTH.currentUser.email);
});

const initialState = {
    silentPrint: getSilentPrint(),
    printerSize: getPrinterSize(),
    tin: undefined,
    isTrainingModActive: getSessionTrainingMode(),
    settings: undefined,
    bankNumber: undefined,
    accountant: undefined,
    userImage: null,
    ticketLogo: null,
    loading: false,
    updating: false,
    uploading: false,
    advertisementText: undefined,
    ticketValidityPeriod: undefined
};

const slice = createSlice({
    name: "settings",
    initialState,
    reducers: {
        turnOffTrainingMode: (state) => {
            setSessionTrainingMode(false);
            state.isTrainingModActive = false;
        },
        turnOnTrainingMode: (state) => {
            setSessionTrainingMode(true);
            state.isTrainingModActive = true;
        },
        changeSilentPrint: (state, {payload}) => {
            setSilentPrint(payload);
            state.silentPrint = payload;
        },
        setNewPrinterSize: (state, {payload}) => {
            setPrinterSize(payload)
            state.printerSize = payload;
        }
    },
    extraReducers: {
        [fetchUserLogoUrl.pending]: (state) => {
            state.loading = true;
        },
        [fetchUserLogoUrl.fulfilled]: (state, {payload}) => {
            state.userImage = payload;
            state.loading = false;
        },
        [fetchUserLogoUrl.rejected]: (state) => {
            state.userImage = undefined;
            state.loading = false;
        },
        [insertUserImage.pending]: (state) => {
            state.uploading = true;
        },
        [insertUserImage.fulfilled]: (state, {payload}) => {
            state.userImage = payload;
            state.uploading = false;
        },
        [insertUserImage.rejected]: (state) => {
            state.uploading = false;
        },
        [fetchBankAccount.fulfilled]: (state, {payload}) => {
            state.bankNumber = payload;
        },
        [fetchAccountant.pending]: (state) => {
            state.loading = true;
        },
        [fetchAccountant.fulfilled]: (state, {payload}) => {
            state.accountant = payload;
            state.loading = false;
        },
        [fetchAccountant.rejected]: (state) => {
            state.loading = false;
        },
        [updateAccountant.fulfilled]: (state, {meta}) => {
            state.accountant = meta.arg;
            state.loading = false;
        },
        [fetchBankAccount.fulfilled]: (state, {payload}) => {
            state.bankNumber = payload;
            state.loading = false;
        },
        [updateBankAccount.fulfilled]: (state, {meta}) => {
            state.bankNumber = meta.arg;
            state.loading = false;
        },
        // fetchSettings
        [fetchSettings.pending]: (state) => {
            state.loading = true;
        },
        [fetchSettings.fulfilled]: (state, {payload}) => {
            const {settings} = payload;
            state.settings = settings;
            state.ticketLogo = settings?.ticketLogo || null;
            state.loading = false;
        },
        [fetchSettings.rejected]: (state) => {
            state.loading = false;
        },
        [updateSettings.fulfilled]: (state, {payload}) => {
            state.settings = payload;
            state.ticketLogo = payload?.ticketLogo || null;
        },
        [updateTicketValidityPeriod.pending]: (state) => {
            state.loading = true;
        },
        [updateTicketValidityPeriod.fulfilled]: (state, {meta}) => {
            const data = {...current(state.settings)}
            data.ticketValidityPeriod = meta.arg
            state.settings = data;
            state.loading = false;
        },
        [updateTicketValidityPeriod.rejected]: (state) => {
            state.loading = false;
        },

        [updateVerificationPrice.pending]: (state) => {
            state.loading = true;
        },
        [updateVerificationPrice.fulfilled]: (state, {meta}) => {
            const data = {...current(state.settings)}
            data.ticketPriceConfirmation = meta.arg
            state.settings = data;
            state.loading = false;
        },
        [updateVerificationPrice.rejected]: (state) => {
            state.loading = false;
        },

        //fetchTIN
        [fetchTIN.fulfilled]: (state, {payload}) => {
            state.tin = payload;
        },
        //fetchAdvertisementText
        [fetchAdvertisementText.fulfilled]: (state, {payload}) => {
            state.advertisementText = payload;
        },
        //updateAdvertisementText
        [updateAdvertisementText.fulfilled]: (state, {meta}) => {
            state.advertisementText = meta.arg;
        }
    }
});

export const {
    changeSilentPrint,
    setNewPrinterSize,
    turnOffTrainingMode,
    turnOnTrainingMode,
    turOnVirtualKeyboard,
    turnOffVirtualKeyboard
} = slice.actions;
// Reducer
export default slice.reducer;
